import { createSharedComposable } from '@vueuse/core'
import { type User } from '@/features/api/types'
import {
	type TelemetryConfig,
	type TelemetryMetadata,
	type CustomDimensions,
	type EventType,
	type TelemetryAdapter,
	type TelemetryTransaction
} from '@/features/telemetry/types'
import { useAmplitudeTelemetryAdapter } from '@/features/telemetry/useAmplitudeTelemetryAdapter'
import { useAPMTelemetryAdapter } from '@/features/telemetry/useAPMTelemetryAdapter'
import { useConsoleTelemetryAdapter } from '@/features/telemetry/useConsoleTelemetryAdapter'
import { useElasticTelemetryAdapter } from '@/features/telemetry/useElasticTelemetryAdapter'
import { getVerifiedTelemetryEvent, SENSITIVE_TELEMETRY_MESSAGE } from '@/features/telemetry/utils'

const { VITE_AMPLITUDE_API_KEY, VITE_ELASTICSEARCH_URL, VITE_ELASTICSEARCH_API_KEY, VITE_ELASTIC_APM_URL, VITE_APP_VERSION, VITE_ENVIRONMENT } =
	import.meta.env

const isEmbedded = () => {
	const searchParams = new URLSearchParams(window.location.search)

	return !!searchParams.get('embedded')
}

const EVENTS_PLANNER_KEYWORD = 'events-planner'

const metadata: TelemetryMetadata = {
	app: {
		version: VITE_APP_VERSION,
		environment: VITE_ENVIRONMENT
	},
	elasticTarget: 'electron-app',
	serviceName: isEmbedded() ? `${EVENTS_PLANNER_KEYWORD}-embedded` : EVENTS_PLANNER_KEYWORD
}

export const getTelemetryConfigData = <T extends keyof TelemetryConfig>(key: T): TelemetryConfig[T] | undefined => {
	const data: TelemetryConfig = {
		AMPLITUDE_API_KEY: VITE_AMPLITUDE_API_KEY,
		ELASTICSEARCH_API_KEY: VITE_ELASTICSEARCH_API_KEY,
		ELASTICSEARCH_URL: VITE_ELASTICSEARCH_URL,
		ELASTIC_APM: VITE_ELASTIC_APM_URL
			? {
					serviceName: metadata.serviceName,
					environment: metadata.app.environment,
					serverUrl: VITE_ELASTIC_APM_URL,
					distributedTracingOrigins: [import.meta.env.VITE_API_URL].filter((url): url is string => !!url),

					ignoreTransactions: [new RegExp(`[^.]*${VITE_ELASTICSEARCH_URL}[^.]*`)]
				}
			: undefined
	}

	return data[key]
}

function createTelemetry() {
	const adapters: TelemetryAdapter[] = [
		useAmplitudeTelemetryAdapter(getTelemetryConfigData, metadata),
		useElasticTelemetryAdapter(getTelemetryConfigData, 'renderer', {
			logger: EVENTS_PLANNER_KEYWORD,
			serviceName: metadata.serviceName,
			eventDataset: EVENTS_PLANNER_KEYWORD
		}),
		useAPMTelemetryAdapter(getTelemetryConfigData),
		useConsoleTelemetryAdapter(metadata)
	]

	function updateAdaptersMetadata() {
		adapters.forEach((adapter) => adapter.setMetadata(metadata))
	}

	updateAdaptersMetadata()

	const trackEvent = (type: EventType, name: string, customProperties?: CustomDimensions) => {
		const epName = `EP | ${name}`
		const verified = getVerifiedTelemetryEvent(name, customProperties)

		if (verified.isSafe) {
			trackSafeEvent(type, epName, customProperties)
		} else {
			trackSafeEvent(type, epName, {
				...verified.redactedPayload,
				isRedacted: true
			})

			// Additionally - log the fact that sensitive trace was found
			trackSafeException(new Error(SENSITIVE_TELEMETRY_MESSAGE), SENSITIVE_TELEMETRY_MESSAGE, {
				fromEvent: epName,
				suspiciousKeywords: verified.suspiciousKeywords
			})
		}
	}

	const trackException = (error: unknown, message: string, customProperties?: CustomDimensions) => {
		const exception = error instanceof Error ? error : new Error(String(error))
		const verified = getVerifiedTelemetryEvent(message, customProperties)

		if (verified.isSafe) {
			trackSafeException(exception, message, customProperties)
		} else {
			trackSafeException(exception, message, {
				...verified.redactedPayload,
				isRedacted: true
			})

			// Additionally - log the fact that sensitive trace was found
			trackSafeException(new Error(SENSITIVE_TELEMETRY_MESSAGE), SENSITIVE_TELEMETRY_MESSAGE, {
				fromEvent: message,
				suspiciousKeywords: verified.suspiciousKeywords
			})
		}
	}

	const trackSafeEvent = (type: EventType, name: string, customProperties?: CustomDimensions) => {
		adapters.forEach((adapter) => adapter.trackEvent(type, name, customProperties))
	}

	const trackSafeException = (exception: Error, message: string, customProperties?: CustomDimensions): void => {
		adapters.forEach((adapter) => adapter.trackException(exception, message, customProperties))
	}

	const startTransaction = (name: string, type: string): TelemetryTransaction | undefined => {
		// A grouping transaction for all adapters
		const transactions = adapters.map((adapter) => adapter.startTransaction(name, type))

		return {
			startSpan(name: string | null, type: string | null) {
				const spans = transactions.map((transaction) => transaction?.startSpan(name, type))

				return {
					end() {
						spans.forEach((span) => span?.end())
					}
				}
			},

			end() {
				transactions.forEach((transaction) => transaction?.end())
			}
		} satisfies TelemetryTransaction
	}

	function setUserData(user?: User) {
		if (user) {
			metadata.user = {
				companyName: user.companyName,
				isPartner: String(user.isPartner),
				username: user.email,
				isTestAccount: String(user.testAccount),
				id: user.id
			}

			updateAdaptersMetadata()
		}
	}

	function setInstallationId(id: string) {
		metadata.app.installationId = id
		updateAdaptersMetadata()
	}

	return {
		trackException,
		trackEvent,
		setUserData,
		setInstallationId,
		startTransaction
	}
}

export const useTelemetry = createSharedComposable(createTelemetry)
